<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-needle me-2"></i>{{ $t('page.immunization', 'Immunization Register (KEPI)') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ coverageStats.active_clients || 0 }}</div><div class="hms-stat-lbl">Active Clients</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ coverageStats.doses_given || 0 }}</div><div class="hms-stat-lbl">Doses (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ coverageStats.doses_missed || 0 }}</div><div class="hms-stat-lbl">Missed/Refused</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ defaulterCount || 0 }}</div><div class="hms-stat-lbl">Defaulters</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ coverageStats.reactions || 0 }}</div><div class="hms-stat-lbl">Adverse Events</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='card' }" @click="tab='card'">
          <i class="mdi mdi-card-account-details me-1"></i>CWC Card
        </button>
        <button class="hms-tab" :class="{ active: tab==='defaulters' }" @click="tab='defaulters'; loadDefaulters()">
          <i class="mdi mdi-alert me-1"></i>Defaulter List
        </button>
        <button class="hms-tab" :class="{ active: tab==='coverage' }" @click="tab='coverage'; loadCoverage()">
          <i class="mdi mdi-chart-bar me-1"></i>Coverage Report
        </button>
      </div>

      <!-- ============ CWC CARD TAB ============ -->
      <div v-if="tab==='card'">
        <div class="d-flex gap-2 mb-3">
          <input v-model="cardSearch" class="hms-input" placeholder="Search child by name / patient no / CWC no" @keyup.enter="findChild" />
          <button class="hms-btn hms-btn-blue" @click="findChild">Search</button>
          <button class="hms-btn hms-btn-success" @click="openRegisterModal" :disabled="!card.patient || card.patient.client_id">
            <i class="mdi mdi-account-plus"></i> Register on KEPI
          </button>
        </div>

        <div v-if="!card.patient" class="hms-empty">
          <i class="mdi mdi-card-account-details-outline"></i>
          <h5>Search a child to view their immunization card</h5>
        </div>

        <div v-else>
          <!-- HEADER -->
          <div class="hms-card mb-3">
            <div class="hms-card-body">
              <div class="d-flex justify-content-between flex-wrap gap-3">
                <div>
                  <div class="hms-name-big">{{ card.patient.first_name }} {{ card.patient.last_name }}</div>
                  <div class="hms-muted-sm">
                    <span v-if="card.patient.cwc_number">{{ card.patient.cwc_number }} · </span>
                    {{ card.patient.patient_no }}
                    <span v-if="card.patient.date_of_birth"> · DOB {{ card.patient.date_of_birth }} ({{ ageString(card.patient.date_of_birth) }})</span>
                  </div>
                  <div class="hms-muted-sm" v-if="card.patient.mother_name || card.patient.mother_phone">
                    Mother: {{ card.patient.mother_name }} <span v-if="card.patient.mother_phone">· {{ card.patient.mother_phone }}</span>
                  </div>
                  <div class="hms-muted-sm" v-if="card.patient.village || card.patient.chv_name">
                    {{ card.patient.village }} <span v-if="card.patient.chv_name">· CHV: {{ card.patient.chv_name }}</span>
                  </div>
                </div>
                <div v-if="!card.patient.client_id">
                  <span class="hms-pill hms-pill-warn">Not on KEPI register</span>
                </div>
              </div>
            </div>
          </div>

          <!-- SCHEDULE TABLE -->
          <div class="hms-card">
            <div class="hms-card-head">Immunization schedule</div>
            <div class="hms-card-body p-0">
              <table class="hms-table">
                <thead>
                  <tr>
                    <th>Vaccine</th><th>Dose</th><th>Route</th>
                    <th>Due date</th><th>Given</th><th>Batch</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(s, i) in card.schedule" :key="i" :class="rowForStatus(s.status)">
                    <td>
                      <strong>{{ s.vaccine_code }}</strong>
                      <div class="hms-muted-sm">{{ s.vaccine_name }}</div>
                    </td>
                    <td>{{ s.dose_number }}/{{ s.total_doses }}</td>
                    <td>{{ s.route || '—' }}</td>
                    <td>{{ s.due_date || '—' }}</td>
                    <td>
                      <span v-if="s.given_record">{{ s.given_record.given_date }}</span>
                      <span v-else>—</span>
                    </td>
                    <td>
                      <span v-if="s.given_record">{{ s.given_record.batch_number || '—' }}</span>
                      <span v-else>—</span>
                    </td>
                    <td><span class="hms-pill" :class="statusPill(s.status)">{{ statusLabel(s.status) }}</span></td>
                    <td>
                      <button v-if="['scheduled','due','overdue'].includes(s.status)" class="hms-btn hms-btn-success hms-btn-sm" @click="openGiveModal(s)">
                        <i class="mdi mdi-needle"></i> Give
                      </button>
                      <button v-if="['scheduled','due','overdue'].includes(s.status)" class="hms-btn-link" @click="openMissModal(s)" title="Mark not given">
                        <i class="mdi mdi-close-octagon"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ DEFAULTERS TAB ============ -->
      <div v-if="tab==='defaulters'">
        <div v-if="!defaulters.length" class="hms-empty">
          <i class="mdi mdi-check-circle"></i>
          <h5>No defaulters</h5>
          <p>All registered children are up to date.</p>
        </div>
        <table v-else class="hms-table">
          <thead>
            <tr>
              <th>CWC #</th><th>Child</th><th>DOB</th><th>Vaccine</th><th>Dose</th>
              <th>Due</th><th>Days late</th><th>Mother / CHV</th><th>Phone</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(d, i) in defaulters" :key="i" :class="d.days_overdue > 60 ? 'row-action' : 'row-warn'">
              <td><strong>{{ d.cwc_number || '—' }}</strong></td>
              <td>{{ d.patient_name }} <span class="hms-muted-sm">{{ d.patient_no }}</span></td>
              <td>{{ d.date_of_birth }}</td>
              <td>{{ d.vaccine_code }}</td>
              <td>{{ d.dose_number }}</td>
              <td>{{ d.due_date }}</td>
              <td><strong :class="d.days_overdue > 60 ? 'st-bad' : ''">{{ d.days_overdue }}d</strong></td>
              <td>{{ d.mother_name || '—' }} <span v-if="d.chv_name" class="hms-muted-sm">/ {{ d.chv_name }}</span></td>
              <td>{{ d.phone || '—' }}</td>
              <td>
                <button class="hms-btn-link" @click="loadByPatientId(d.patient_id)" title="Open card"><i class="mdi mdi-card-account-details"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ COVERAGE TAB ============ -->
      <div v-if="tab==='coverage'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="coveragePeriod" class="hms-input" @change="loadCoverage" style="max-width:200px">
            <option value="today">Today</option>
            <option value="this_week">This week</option>
            <option value="this_month">This month</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-card">
          <div class="hms-card-head">Doses given by vaccine — {{ periodLabel }}</div>
          <div class="hms-card-body">
            <div v-for="v in byVaccine" :key="v.code" class="hms-cov-row">
              <div class="hms-cov-label">
                <strong>{{ v.code }}</strong>
                <span class="hms-muted-sm">{{ v.name }}</span>
              </div>
              <div class="hms-cov-bar">
                <div class="hms-cov-fill" :style="{ width: covPct(v.doses_given) + '%' }"></div>
              </div>
              <div class="hms-cov-num">{{ v.doses_given || 0 }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== REGISTER MODAL ===== -->
    <div v-if="registerModal" class="hms-modal-bg" @click.self="registerModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Register on KEPI</h5><button class="hms-icon-btn" @click="registerModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">
            Patient: <strong>{{ card.patient ? (card.patient.first_name + ' ' + card.patient.last_name) : '' }}</strong>
            <span v-if="card.patient && card.patient.date_of_birth"> · DOB {{ card.patient.date_of_birth }}</span>
          </div>
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Client type</label>
              <select v-model="register.client_type" class="hms-input">
                <option value="child">Child</option><option value="adolescent">Adolescent</option>
                <option value="wcba">Woman of childbearing age</option><option value="adult">Adult</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Birth weight (g)</label><input v-model.number="register.birth_weight_g" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Birth facility</label><input v-model="register.birth_facility" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Mother name</label><input v-model="register.mother_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Mother phone</label><input v-model="register.mother_phone" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Village / Location</label><input v-model="register.village" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV name</label><input v-model="register.chv_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV phone</label><input v-model="register.chv_phone" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="registerModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveRegister">{{ saving?'Saving…':'Register' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== GIVE DOSE MODAL ===== -->
    <div v-if="giveModal" class="hms-modal-bg" @click.self="giveModal=false">
      <div class="hms-modal" style="width:540px">
        <div class="hms-modal-head"><h5>Give {{ giveSlot.vaccine_code }} dose {{ giveSlot.dose_number }}</h5><button class="hms-icon-btn" @click="giveModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Batch number *</label><input v-model="give.batch_number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Expiry date</label><input v-model="give.expiry_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Manufacturer</label><input v-model="give.manufacturer" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Site</label><input v-model="give.site" class="hms-input" :placeholder="giveSlot.default_site" /></div>
            <div class="col-6"><label class="hms-label">Route</label><input v-model="give.route" class="hms-input" :placeholder="giveSlot.route" /></div>
            <div class="col-12"><label class="hms-label">Adverse reaction (if any)</label><input v-model="give.reaction" class="hms-input" placeholder="None / fever / local reaction…" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="give.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="giveModal=false">Cancel</button>
          <SaveAndQueue ref="sqImm" from-point="immunization" :saving="saving" :disabled="!give.batch_number"
                        :save-label="'Save Dose'" save-label-with-queue="Save & queue to ___" @save="saveGive" />
        </div>
      </div>
    </div>

    <!-- ===== MARK NOT GIVEN MODAL ===== -->
    <div v-if="missModal" class="hms-modal-bg" @click.self="missModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>Mark not given — {{ missSlot.vaccine_code }} dose {{ missSlot.dose_number }}</h5><button class="hms-icon-btn" @click="missModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-form-group"><label class="hms-label">Status</label>
            <select v-model="miss.status" class="hms-input">
              <option value="missed">Missed</option>
              <option value="refused">Refused</option>
              <option value="contraindicated">Contraindicated</option>
              <option value="transferred">Transferred elsewhere</option>
            </select>
          </div>
          <div class="hms-form-group"><label class="hms-label">Reason *</label><textarea v-model="miss.reason_not_given" class="hms-input" rows="2"></textarea></div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="missModal=false">Cancel</button>
          <button class="hms-btn hms-btn-blue" :disabled="saving || !miss.reason_not_given" @click="saveMiss">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ImmunizationRegister",
  components: { Navigation, SaveAndQueue },
  data() {
    return {
      tab: 'card', saving: false,
      cardSearch: '',
      card: { patient: null, schedule: [] },

      defaulters: [], defaulterCount: 0,

      coveragePeriod: 'this_month',
      byVaccine: [], coverageStats: {},

      registerModal: false, register: this.blankRegister(),
      giveModal: false, give: this.blankGive(), giveSlot: {},
      missModal: false, miss: { status: 'missed', reason_not_given: '' }, missSlot: {}
    };
  },
  computed: {
    periodLabel() {
      return ({ today: 'Today', this_week: 'This week', this_month: 'This month', this_year: 'This year' })[this.coveragePeriod] || '';
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankRegister() { return { client_type: 'child', birth_weight_g: null, birth_facility: '', mother_name: '', mother_phone: '', village: '', chv_name: '', chv_phone: '' }; },
    blankGive() { return { batch_number: '', expiry_date: '', manufacturer: '', site: '', route: '', reaction: '', notes: '' }; },

    async loadAll() {
      await Promise.all([this.loadDefaulters(), this.loadCoverage()]);
    },

    async findChild() {
      const q = (this.cardSearch || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (!p) { alert('No patient found.'); return; }
        await this.loadByPatientId(p.patient_id);
      }
    },
    async loadByPatientId(patientId) {
      this.tab = 'card';
      try {
        const r = await executeJson({ function: 'getImmunizationSchedule', patient_id: patientId }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.card.patient  = d.Patient || null;
          this.card.schedule = d.Schedule || [];
        }
      } catch (e) { console.error(e); }
    },
    openRegisterModal() {
      if (!this.card.patient) return;
      this.register = this.blankRegister();
      if (this.card.patient.mother_name) this.register.mother_name = this.card.patient.mother_name;
      this.registerModal = true;
    },
    async saveRegister() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'registerImmunizationClient', patient_id: this.card.patient.patient_id, ...this.register }, hms);
        if (r.data && r.data.success) {
          this.registerModal = false;
          await this.loadByPatientId(this.card.patient.patient_id);
          await this.loadAll();
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openGiveModal(slot) {
      this.giveSlot = slot;
      this.give = this.blankGive();
      this.give.site  = slot.default_site || '';
      this.give.route = slot.route || '';
      this.giveModal = true;
    },
    async saveGive() {
      this.saving = true;
      try {
        const r = await executeJson({
          function: 'recordImmunization',
          patient_id: this.card.patient.patient_id,
          vaccine_id: this.giveSlot.vaccine_id,
          dose_number: this.giveSlot.dose_number,
          status: 'given',
          ...this.give
        }, hms);
        if (r.data && r.data.success) {
          // Sharon F#3 #18 — if user picked a destination on the SaveAndQueue dropdown, route there
          if (this.$refs.sqImm && this.card.patient.patient_id) {
            try { await this.$refs.sqImm.queueIfSelected(this.card.patient.patient_id); } catch (e) { /* non-fatal */ }
          }
          this.giveModal = false;
          await this.loadByPatientId(this.card.patient.patient_id);
          await this.loadAll();
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openMissModal(slot) {
      this.missSlot = slot;
      this.miss = { status: 'missed', reason_not_given: '' };
      this.missModal = true;
    },
    async saveMiss() {
      this.saving = true;
      try {
        const r = await executeJson({
          function: 'recordImmunization',
          patient_id: this.card.patient.patient_id,
          vaccine_id: this.missSlot.vaccine_id,
          dose_number: this.missSlot.dose_number,
          ...this.miss
        }, hms);
        if (r.data && r.data.success) {
          this.missModal = false;
          await this.loadByPatientId(this.card.patient.patient_id);
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadDefaulters() {
      try {
        const r = await executeJson({ function: 'getImmunizationDefaulters' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.defaulters = d.Defaulters || [];
          this.defaulterCount = d.TotalDefaulters || 0;
        }
      } catch (e) { console.error(e); }
    },
    async loadCoverage() {
      try {
        const r = await executeJson({ function: 'getImmunizationCoverage', period: this.coveragePeriod }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.byVaccine = d.ByVaccine || [];
          this.coverageStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    statusPill(s) {
      return ({ scheduled: 'hms-pill-muted', due: 'hms-pill-info', overdue: 'hms-pill-danger',
                given: 'hms-pill-success', missed: 'hms-pill-warn', refused: 'hms-pill-warn',
                contraindicated: 'hms-pill-warn', transferred: 'hms-pill-muted' })[s] || '';
    },
    statusLabel(s) { return ({ scheduled: 'Scheduled', due: 'Due', overdue: 'Overdue', given: 'Given', missed: 'Missed', refused: 'Refused', contraindicated: 'Contra.', transferred: 'Transferred' })[s] || s; },
    rowForStatus(s) { return ({ overdue: 'row-action', due: 'row-warn', given: '' })[s] || ''; },
    covPct(n) {
      if (!n || !this.byVaccine.length) return 0;
      const max = Math.max(...this.byVaccine.map(v => parseInt(v.doses_given || 0)));
      return max > 0 ? Math.round((parseInt(n) / max) * 100) : 0;
    },
    ageString(dob) {
      if (!dob) return '';
      const d = new Date(dob.replace ? dob.replace(' ','T') : dob);
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (days < 60)   return days + 'd';
      if (days < 730)  return Math.floor(days / 30) + 'mo';
      return Math.floor(days / 365) + 'y';
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-success { color: #276749; } .hms-stat-info { color: #2c5282; }
.hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #a0aec0; }

.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }

.hms-cov-row { display: grid; grid-template-columns: 200px 1fr 60px; gap: 12px; align-items: center; padding: 6px 0; border-bottom: 1px solid #edf2f7; }
.hms-cov-label strong { display: block; }
.hms-cov-bar  { background: #edf2f7; border-radius: 4px; height: 14px; overflow: hidden; }
.hms-cov-fill { background: linear-gradient(90deg, #4299e1, #2b6cb0); height: 100%; }
.hms-cov-num  { text-align: right; font-weight: 700; color: #2d3748; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.st-bad { color: #c53030; font-weight: 600; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
</style>
